/**
 * Grand Quack Auto — tutorial / onboarding system.
 *
 * Two beats, per-session only (no localStorage):
 *   1. Move — 4 cardinal chevrons pulse outward around the duck plus a GTA
 *      mission-brief card. Hides once the duck walks TUTORIAL.moveHideDistance
 *      or TUTORIAL.moveHideElapsedSeconds have passed.
 *   2. Quack-near-NPC — within TUTORIAL.npcTriggerRadius of any NPC, the four
 *      chevrons vanish, a single down-pointing cone appears above the NPC, and
 *      a [SPACE] QUACK key badge shows. Fades on the first successful quack.
 *
 * Tunables live in config.ts TUTORIAL. DOM elements (#mission-brief,
 * #key-prompt, .mission-title, .mission-sub, .key-badge) live in index.html.
 * 3D markers live in src/ui/tutorialMarker.ts.
 */
import * as THREE from 'three';
import { TUTORIAL } from '../config';
import type { System, World } from '../types';
import {
  disposeTutorialMarkers, ensureTutorialMarkers, setTutorialMode,
  setTutorialOpacity, updateMoveCue, updateNpcCue, __resetTutorialMarkers,
} from '../ui/tutorialMarker';
import { getActiveNpcs } from './npcSystem';

type Phase = 'move' | 'npc' | 'fading' | 'done';

const DISPATCH_NPC = [
  "Dispatch: Hit [SPACE]. Show 'em who runs this town.",
  'Dispatch: One quack, one less tax-payer.',
  'Dispatch: These citizens need QUACK-ilitation.',
];

let phase: Phase = 'move';
let spawnPos: THREE.Vector3 | null = null;
let startTime = 0;
let fadeStart = 0;
let missionEl: HTMLElement | null = null;
let keyPromptEl: HTMLElement | null = null;
let targetNpc: THREE.Object3D | null = null;

function ensureDom(): void {
  if (!missionEl) missionEl = document.getElementById('mission-brief');
  if (!keyPromptEl) keyPromptEl = document.getElementById('key-prompt');
}

function showMissionBrief(title: string, sub: string): void {
  ensureDom();
  if (!missionEl) return;
  missionEl.innerHTML = `<span class="mission-title">${title}</span><span class="mission-sub">${sub}</span>`;
  missionEl.classList.add('is-visible');
}

function hideMissionBrief(): void {
  ensureDom();
  missionEl?.classList.remove('is-visible');
}

function showKeyPrompt(keyLabel: string, action: string): void {
  ensureDom();
  if (!keyPromptEl) return;
  keyPromptEl.innerHTML = `<span class="key-badge">${keyLabel}</span><span>${action}</span>`;
  keyPromptEl.classList.add('is-visible');
}

function hideKeyPrompt(): void {
  ensureDom();
  keyPromptEl?.classList.remove('is-visible');
}

function nearestNpc(world: World): THREE.Object3D | null {
  const npcs = getActiveNpcs();
  let best: THREE.Object3D | null = null;
  let bestDist: number = TUTORIAL.npcTriggerRadius;
  for (const npc of npcs) {
    const d = npc.group.position.distanceTo(world.duck.position);
    if (d < bestDist) { bestDist = d; best = npc.group; }
  }
  return best;
}

export const tutorialSystem: System = {
  name: 'tutorial',

  fixedUpdate(_dt: number, world: World) {
    if (phase === 'done') return;

    if (!spawnPos) {
      spawnPos = world.duck.position.clone();
      startTime = world.time.elapsed;
      showMissionBrief('OBJECTIVE: WADDLE', 'Dispatch: Move it, duck. WASD to roll out.');
    }

    if (phase === 'npc' && world.input.quack) {
      phase = 'fading';
      fadeStart = world.time.elapsed;
      hideMissionBrief(); hideKeyPrompt();
      return;
    }

    if (phase === 'fading') {
      if (world.time.elapsed - fadeStart >= TUTORIAL.markerFadeSec) {
        phase = 'done';
        disposeTutorialMarkers(world.scene);
      }
      return;
    }

    const near = nearestNpc(world);
    if (near) {
      if (phase !== 'npc') {
        phase = 'npc';
        const line = DISPATCH_NPC[Math.floor(Math.random() * DISPATCH_NPC.length)]!;
        showMissionBrief('OBJECTIVE: QUACK THE CITIZENS', line);
        showKeyPrompt('SPACE', 'QUACK');
      }
      targetNpc = near;
      return;
    }

    if (phase === 'move') {
      const moved = world.duck.position.distanceTo(spawnPos);
      const elapsed = world.time.elapsed - startTime;
      if (moved >= TUTORIAL.moveHideDistance || elapsed >= TUTORIAL.moveHideElapsedSeconds) {
        phase = 'fading';
        fadeStart = world.time.elapsed;
        hideMissionBrief();
      }
    } else if (phase === 'npc') {
      phase = 'move';
      targetNpc = null;
      showMissionBrief('OBJECTIVE: WADDLE', 'Dispatch: Move it, duck. WASD to roll out.');
      hideKeyPrompt();
    }
  },

  render(dt: number, _alpha: number, world: World) {
    if (phase === 'done') return;
    ensureTutorialMarkers(world.scene);

    const showNpc = (phase === 'npc' || (phase === 'fading' && !!targetNpc));
    setTutorialMode(showNpc);

    const opacity = phase === 'fading'
      ? Math.max(0, 1 - (world.time.elapsed - fadeStart) / TUTORIAL.markerFadeSec)
      : Math.min(1, (world.time.elapsed - startTime) / 0.4);
    setTutorialOpacity(opacity);

    if (showNpc && targetNpc) {
      updateNpcCue(targetNpc, world.time.elapsed, dt);
    } else {
      updateMoveCue(world.duck, world.time.elapsed, dt);
    }
  },
};

export function __resetTutorial(): void {
  phase = 'move';
  spawnPos = null;
  targetNpc = null;
  missionEl = null; keyPromptEl = null;
  __resetTutorialMarkers();
}
